// Presence ("open to taste together", docs/design/social-plan.md section 3): the windows and the
// relative "last active" buckets, pure so the edge, the PWA and their tests share one rulebook.

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

/** How long someone counts as open after the app was last in the foreground. */
export const PRESENCE_OPEN_MS = 4 * HOUR_MS;
/** A foregrounded app reports itself this often; hidden apps report nothing. */
export const PRESENCE_HEARTBEAT_MS = 15 * MINUTE_MS;
/** Heartbeats closer together than this are noise (a flapping tab), however they come. */
export const PRESENCE_MIN_GAP_MS = MINUTE_MS;
/** "Last active" is hidden once it is older than this. */
export const LAST_ACTIVE_MAX_MS = 8 * WEEK_MS;
/** Under this a person reads as "just now" rather than "3m ago". */
export const LAST_ACTIVE_NOW_MS = 5 * MINUTE_MS;

/** A session you said yes to opens you this long before it starts and this long after it ends. */
export const SESSION_PRESENCE_MARGIN_MS = 4 * HOUR_MS;
/** Sessions carry no planned end; until the host ends one, it is assumed to last this long. */
export const SESSION_ASSUMED_LENGTH_MS = 2 * HOUR_MS;

export function openUntilFor(lastSeenAt: string): string {
  return new Date(Date.parse(lastSeenAt) + PRESENCE_OPEN_MS).toISOString();
}

/** Open through the last instant of the window, so a sighting at exactly 4 h still counts. */
export function isOpenToTaste(openUntil: string | null, nowMs: number): boolean {
  return openUntil !== null && nowMs <= Date.parse(openUntil);
}

/** The ISO bounds a store compares session columns against (ISO strings sort as instants). */
export function sessionPresenceBounds(nowMs: number): {
  /** A scheduled session opens you while its start is at most this far ahead: not after this. */
  scheduledUntil: string;
  /** ...and while its start is no earlier than this (assumed length plus the margin back). */
  scheduledFrom: string;
  /** An ended session keeps you open when it ended no earlier than this. */
  endedFrom: string;
} {
  return {
    scheduledUntil: new Date(nowMs + SESSION_PRESENCE_MARGIN_MS).toISOString(),
    scheduledFrom: new Date(
      nowMs - SESSION_PRESENCE_MARGIN_MS - SESSION_ASSUMED_LENGTH_MS,
    ).toISOString(),
    endedFrom: new Date(nowMs - SESSION_PRESENCE_MARGIN_MS).toISOString(),
  };
}

export interface PresenceSessionTimes {
  status: string;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
}

/** Whether a session the person said yes to currently shows them as open. */
export function isInSessionPresenceWindow(session: PresenceSessionTimes, nowMs: number): boolean {
  const bounds = sessionPresenceBounds(nowMs);
  if (session.status === "live") return true;
  if (session.status === "ended") {
    // Ended without ever starting means cancelled.
    return (
      session.startedAt !== null && session.endedAt !== null && session.endedAt >= bounds.endedFrom
    );
  }
  if (session.status === "scheduled" && session.scheduledAt !== null) {
    return (
      session.scheduledAt <= bounds.scheduledUntil && session.scheduledAt >= bounds.scheduledFrom
    );
  }
  return false;
}

/** A live tasting shows for at most this long after it began, finished or not: a run someone forgot
 * to close must not read as "tasting now" for days. */
export const ACTIVITY_MAX_MS = 4 * HOUR_MS;
/** Activity is only believed while the app keeps reporting: one heartbeat plus a few minutes of
 * grace. A hidden app writes nothing, so its tasting fades from view instead of being wiped. */
export const ACTIVITY_FRESH_MS = PRESENCE_HEARTBEAT_MS + 5 * MINUTE_MS;

/** What a person is doing: tasting on their own (no details ever leave the device), in a session,
 * or in a shared flight that has no session around it. */
export const PRESENCE_ACTIVITY_KINDS = ["solo", "session", "flight"] as const;
export type PresenceActivityKind = (typeof PRESENCE_ACTIVITY_KINDS)[number];

/** The pages of the guided tasting a shared activity can say it is on ("bij Neus"); a page name
 * only, never anything typed or picked on it. */
export const PRESENCE_STEPS = ["look", "smell", "taste", "structure", "summary"] as const;
export type PresenceStep = (typeof PRESENCE_STEPS)[number];

/** The instant a tasting that began at `startedAt` stops showing, whatever else is true. */
export function activityUntilFor(startedAt: string): string {
  return new Date(Date.parse(startedAt) + ACTIVITY_MAX_MS).toISOString();
}

/**
 * The start the edge stores for a reported one: a start in the future (a fast clock) counts as now,
 * and one already past the cap is refused, so a stale run cannot be reported back to life.
 */
export function clampActivityStart(reported: string, nowMs: number): string | undefined {
  const at = Date.parse(reported);
  if (Number.isNaN(at) || nowMs - at > ACTIVITY_MAX_MS) return undefined;
  return new Date(Math.min(at, nowMs)).toISOString();
}

export interface ActivityLiveness {
  /** When the stored activity stops showing (4 h after its start). */
  until: string | null;
  /** The person's last heartbeat. */
  lastSeenAt: string | null;
  /** The session ended or the flight is done: a tasting ends with what it was part of. */
  finished: boolean;
}

/** Whether a stored activity still reads as "tasting now": not finished, inside its 4 h, and the
 * app reported itself recently. All bounds are inclusive, like the open window. */
export function isActivityLive(activity: ActivityLiveness, nowMs: number): boolean {
  if (activity.finished || activity.until === null || activity.lastSeenAt === null) return false;
  if (nowMs > Date.parse(activity.until)) return false;
  return nowMs - Date.parse(activity.lastSeenAt) <= ACTIVITY_FRESH_MS;
}

export type LastActiveBucket =
  | { kind: "now" }
  | { kind: "ago"; unit: "minute" | "hour" | "day" | "week"; value: number };

/** Sorts a last-seen time into the unit a person says it in; undefined once it says nothing useful. */
export function lastActiveBucket(
  lastSeenAt: string | null,
  nowMs: number,
): LastActiveBucket | undefined {
  if (lastSeenAt === null) return undefined;
  const seen = Date.parse(lastSeenAt);
  if (Number.isNaN(seen)) return undefined;
  const age = nowMs - seen;
  if (age > LAST_ACTIVE_MAX_MS) return undefined;
  if (age < LAST_ACTIVE_NOW_MS) return { kind: "now" };
  if (age < HOUR_MS) return { kind: "ago", unit: "minute", value: Math.floor(age / MINUTE_MS) };
  if (age < DAY_MS) return { kind: "ago", unit: "hour", value: Math.floor(age / HOUR_MS) };
  if (age < WEEK_MS) return { kind: "ago", unit: "day", value: Math.floor(age / DAY_MS) };
  return { kind: "ago", unit: "week", value: Math.floor(age / WEEK_MS) };
}
